<template>
  <div data-testid="inventory-lifecycle-section">
    <!-- Manual/custom-feed: plain operational fields, no ownership UI. -->
    <template v-if="!feedBacked">
      <v-row class="edit-vehicle-section-field-grid" data-testid="inventory-lifecycle-manual-fields">
        <v-col cols="12" md="4">
          <div class="edit-vehicle-field-block">
            <v-select
              :model-value="inventoryState"
              :items="inventoryStateOptions"
              item-title="title"
              item-value="value"
              label="Inventory State"
              variant="outlined"
              density="comfortable"
              hide-details
              data-testid="inventory-state-control-select"
              @update:model-value="emit('update:inventoryState', $event)"
            />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="edit-vehicle-field-block">
            <v-select
              :model-value="inventoryUsageType"
              :items="inventoryUsageTypeOptions"
              item-title="title"
              item-value="value"
              label="Usage Type"
              variant="outlined"
              density="comfortable"
              hide-details
              data-testid="inventory-usage-type-control-select"
              @update:model-value="emit('update:inventoryUsageType', $event)"
            />
          </div>
        </v-col>
        <v-col v-if="showManualWebsiteVisibility" cols="12" md="4">
          <div
            class="edit-vehicle-field-block"
            data-testid="edit-vehicle-website-visibility-after-publish"
          >
            <div class="text-body-2 font-weight-medium mb-1">
              Website Visibility after publish
            </div>
            <v-radio-group
              :model-value="websiteVisibility"
              inline
              hide-details
              data-testid="edit-vehicle-website-visibility-radio-group"
              @update:model-value="onWebsiteVisibilityUpdate"
            >
              <v-radio
                label="Visible"
                :value="WEBSITE_VISIBILITY.VISIBLE"
                data-testid="edit-vehicle-website-visibility-visible"
              />
              <v-radio
                label="Hidden"
                :value="WEBSITE_VISIBILITY.HIDDEN"
                data-testid="edit-vehicle-website-visibility-hidden"
              />
            </v-radio-group>
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="showManualWebsiteVisibility && websiteVisibility === WEBSITE_VISIBILITY.HIDDEN"
        class="edit-vehicle-section-field-grid mt-1"
        data-testid="edit-vehicle-website-visibility-hidden-fields"
      >
        <v-col cols="12" md="6">
          <v-select
            :model-value="websiteVisibilityReason"
            :items="websiteVisibilityReasonOptions"
            item-title="label"
            item-value="value"
            label="Initial hidden reason"
            variant="outlined"
            density="comfortable"
            data-testid="edit-vehicle-website-visibility-reason-select"
            :error="Boolean(websiteVisibilityFieldErrors?.websiteVisibilityReason)"
            :error-messages="
              websiteVisibilityFieldErrors?.websiteVisibilityReason
                ? [websiteVisibilityFieldErrors.websiteVisibilityReason]
                : []
            "
            @update:model-value="onWebsiteVisibilityReasonUpdate"
          />
        </v-col>
        <v-col v-if="websiteVisibilityReason === 'OTHER'" cols="12" md="6">
          <v-text-field
            :model-value="websiteVisibilityReasonNotes"
            label="Please specify"
            variant="outlined"
            density="comfortable"
            data-testid="edit-vehicle-website-visibility-reason-notes"
            :error="Boolean(websiteVisibilityFieldErrors?.websiteVisibilityReasonNotes)"
            :error-messages="
              websiteVisibilityFieldErrors?.websiteVisibilityReasonNotes
                ? [websiteVisibilityFieldErrors.websiteVisibilityReasonNotes]
                : []
            "
            @update:model-value="onWebsiteVisibilityReasonNotesUpdate"
          />
        </v-col>
      </v-row>
    </template>

    <!-- Feed-backed: durable ownership controls (AUTO/MANUAL). -->
    <v-row
      v-else
      class="edit-vehicle-section-field-grid"
      data-testid="inventory-lifecycle-feed-backed-fields"
    >
      <v-col cols="12" md="6">
        <div
          class="edit-vehicle-field-block lifecycle-field-block"
          :class="inventoryStateFieldBlockClass"
          data-testid="inventory-state-lifecycle-control"
        >
          <div class="lifecycle-field-block__header">
            <div class="lifecycle-field-block__title-row">
              <span class="lifecycle-field-block__title">Inventory State</span>
              <v-chip
                v-if="inventoryStateChangeChipLabel"
                size="x-small"
                :color="inventoryStateChangeChipColor"
                variant="tonal"
                class="lifecycle-unsaved-chip"
                data-testid="inventory-state-change-chip"
              >
                {{ inventoryStateChangeChipLabel }}
              </v-chip>
            </div>
            <v-btn-toggle
              :model-value="inventoryStateMode"
              class="lifecycle-mode-toggle"
              mandatory
              density="compact"
              variant="outlined"
              divided
              data-testid="inventory-state-mode-segmented"
              @update:model-value="setInventoryStateMode"
            >
              <v-btn value="AUTO" size="small" data-testid="inventory-state-mode-auto">
                Automatic
              </v-btn>
              <v-btn value="MANUAL" size="small" data-testid="inventory-state-mode-manual">
                Manual
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-select
            :model-value="inventoryState"
            :items="inventoryStateOptions"
            item-title="title"
            item-value="value"
            label="Inventory State"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="inventoryStateMode === 'AUTO'"
            data-testid="inventory-state-control-select"
            @update:model-value="setInventoryState"
          />

          <div
            v-if="inventoryStatePendingCopy"
            class="lifecycle-field-block__status"
            :class="inventoryStatePendingCopyClass"
            data-testid="inventory-state-pending-copy"
          >
            {{ inventoryStatePendingCopy }}
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <div
          class="edit-vehicle-field-block lifecycle-field-block"
          :class="inventoryUsageTypeFieldBlockClass"
          data-testid="inventory-usage-type-lifecycle-control"
        >
          <div class="lifecycle-field-block__header">
            <div class="lifecycle-field-block__title-row">
              <span class="lifecycle-field-block__title">Usage Type</span>
              <v-chip
                v-if="inventoryUsageTypeChangeChipLabel"
                size="x-small"
                :color="inventoryUsageTypeChangeChipColor"
                variant="tonal"
                class="lifecycle-unsaved-chip"
                data-testid="inventory-usage-type-change-chip"
              >
                {{ inventoryUsageTypeChangeChipLabel }}
              </v-chip>
            </div>
            <v-btn-toggle
              :model-value="inventoryUsageTypeMode"
              class="lifecycle-mode-toggle"
              mandatory
              density="compact"
              variant="outlined"
              divided
              data-testid="inventory-usage-type-mode-segmented"
              @update:model-value="setInventoryUsageTypeMode"
            >
              <v-btn value="AUTO" size="small" data-testid="inventory-usage-type-mode-auto">
                Automatic
              </v-btn>
              <v-btn value="MANUAL" size="small" data-testid="inventory-usage-type-mode-manual">
                Manual
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-select
            :model-value="inventoryUsageType"
            :items="inventoryUsageTypeOptions"
            item-title="title"
            item-value="value"
            label="Usage Type"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="inventoryUsageTypeMode === 'AUTO'"
            data-testid="inventory-usage-type-control-select"
            @update:model-value="setInventoryUsageType"
          />

          <div
            v-if="inventoryUsageTypePendingCopy"
            class="lifecycle-field-block__status"
            :class="inventoryUsageTypePendingCopyClass"
            data-testid="inventory-usage-type-pending-copy"
          >
            {{ inventoryUsageTypePendingCopy }}
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WEBSITE_VISIBILITY, type WebsiteVisibility } from '@jazel-auto/ise-contracts/dist/inventory.js'
import type {
  InventoryOwnershipMode,
  InventoryState,
  InventoryUsageType,
} from '@/services/inventory'
import { MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS } from '@/utils/manualVehicleWebsiteVisibility'
import {
  buildLifecyclePendingCopy,
  normalizeOwnershipMode,
  type InventoryLifecyclePendingChangeState,
} from '@/utils/inventoryLifecycleOwnership'
import '@/styles/edit-vehicle-field-blocks.css'

type SelectOption<T extends string = string> = {
  title: string
  value: T
}

type LifecycleFieldBlockVisualState = 'none' | 'unsaved' | 'draft'

const props = defineProps<{
  feedBacked: boolean
  inventoryState?: InventoryState
  inventoryStateMode?: InventoryOwnershipMode
  inventoryUsageType?: InventoryUsageType
  inventoryUsageTypeMode?: InventoryOwnershipMode
  automaticInventoryState?: InventoryState
  automaticInventoryUsageType?: InventoryUsageType
  baselineInventoryState?: InventoryState
  baselineInventoryStateMode?: InventoryOwnershipMode
  inventoryStatePendingChangeState?: InventoryLifecyclePendingChangeState
  baselineInventoryUsageType?: InventoryUsageType
  baselineInventoryUsageTypeMode?: InventoryOwnershipMode
  inventoryUsageTypePendingChangeState?: InventoryLifecyclePendingChangeState
  inventoryStateOptions: SelectOption<InventoryState>[]
  inventoryUsageTypeOptions: SelectOption<InventoryUsageType>[]
  showManualWebsiteVisibility?: boolean
  websiteVisibility?: WebsiteVisibility
  websiteVisibilityReason?: string
  websiteVisibilityReasonNotes?: string
  websiteVisibilityFieldErrors?: Record<string, string>
}>()

const emit = defineEmits<{
  'update:inventoryState': [value: InventoryState]
  'update:inventoryStateMode': [value: InventoryOwnershipMode]
  'update:inventoryUsageType': [value: InventoryUsageType]
  'update:inventoryUsageTypeMode': [value: InventoryOwnershipMode]
  'update:websiteVisibility': [value: WebsiteVisibility]
  'update:websiteVisibilityReason': [value: string]
  'update:websiteVisibilityReasonNotes': [value: string]
  'clear-website-visibility-field-error': [field: string]
}>()

const websiteVisibilityReasonOptions = MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS

const onWebsiteVisibilityReasonUpdate = (value: string) => {
  emit('update:websiteVisibilityReason', value)
  emit('clear-website-visibility-field-error', 'websiteVisibilityReason')
  emit('clear-website-visibility-field-error', 'websiteVisibilityReasonNotes')
}

const onWebsiteVisibilityUpdate = (value: WebsiteVisibility | null) => {
  if (!value) return
  emit('update:websiteVisibility', value)
}

const onWebsiteVisibilityReasonNotesUpdate = (value: string) => {
  emit('update:websiteVisibilityReasonNotes', value)
  emit('clear-website-visibility-field-error', 'websiteVisibilityReasonNotes')
}

const inventoryStateMode = computed(() => normalizeOwnershipMode(props.inventoryStateMode))
const inventoryUsageTypeMode = computed(() => normalizeOwnershipMode(props.inventoryUsageTypeMode))

const optionLabel = <T extends string>(options: SelectOption<T>[], value: T | undefined): string => {
  return options.find((option) => option.value === value)?.title ?? String(value ?? 'Not set')
}

const inventoryStateLabel = computed(() =>
  optionLabel(props.inventoryStateOptions, props.inventoryState),
)
const inventoryUsageTypeLabel = computed(() =>
  optionLabel(props.inventoryUsageTypeOptions, props.inventoryUsageType),
)

const inventoryStatePendingCopy = computed(() => {
  if (!props.feedBacked) return null
  return buildLifecyclePendingCopy({
    label: 'Inventory State',
    valueLabel: inventoryStateLabel.value,
    mode: inventoryStateMode.value,
    baselineMode: normalizeOwnershipMode(props.baselineInventoryStateMode),
    valueChanged: props.baselineInventoryState !== props.inventoryState,
    changeState: props.inventoryStatePendingChangeState,
  })
})

const inventoryUsageTypePendingCopy = computed(() => {
  if (!props.feedBacked) return null
  return buildLifecyclePendingCopy({
    label: 'Usage Type',
    valueLabel: inventoryUsageTypeLabel.value,
    mode: inventoryUsageTypeMode.value,
    baselineMode: normalizeOwnershipMode(props.baselineInventoryUsageTypeMode),
    valueChanged: props.baselineInventoryUsageType !== props.inventoryUsageType,
    changeState: props.inventoryUsageTypePendingChangeState,
  })
})

const resolveFieldBlockVisualState = (
  pendingCopy: string | null,
  changeState: InventoryLifecyclePendingChangeState | undefined,
): LifecycleFieldBlockVisualState => {
  if (!pendingCopy) return 'none'
  return changeState === 'unsaved' ? 'unsaved' : 'draft'
}

const inventoryStateFieldBlockVisualState = computed(() =>
  resolveFieldBlockVisualState(
    inventoryStatePendingCopy.value,
    props.inventoryStatePendingChangeState,
  ),
)
const inventoryUsageTypeFieldBlockVisualState = computed(() =>
  resolveFieldBlockVisualState(
    inventoryUsageTypePendingCopy.value,
    props.inventoryUsageTypePendingChangeState,
  ),
)

const lifecycleFieldBlockClass = (
  visualState: LifecycleFieldBlockVisualState,
): Record<string, boolean> => ({
  'edit-vehicle-field-block--unsaved': visualState === 'unsaved',
  'edit-vehicle-field-block--pending': visualState === 'draft',
})

const inventoryStateFieldBlockClass = computed(() =>
  lifecycleFieldBlockClass(inventoryStateFieldBlockVisualState.value),
)
const inventoryUsageTypeFieldBlockClass = computed(() =>
  lifecycleFieldBlockClass(inventoryUsageTypeFieldBlockVisualState.value),
)

const lifecycleChangeChipLabel = (
  visualState: LifecycleFieldBlockVisualState,
): string | null => {
  if (visualState === 'unsaved') return 'Unsaved'
  if (visualState === 'draft') return 'Draft saved'
  return null
}

const lifecycleChangeChipColor = (
  visualState: LifecycleFieldBlockVisualState,
): string | undefined => {
  if (visualState === 'unsaved') return 'warning'
  if (visualState === 'draft') return 'info'
  return undefined
}

const lifecyclePendingCopyClass = (
  visualState: LifecycleFieldBlockVisualState,
): Record<string, boolean> => ({
  'lifecycle-field-block__status--unsaved': visualState === 'unsaved',
  'lifecycle-field-block__status--draft': visualState === 'draft',
})

const inventoryStateChangeChipLabel = computed(() =>
  lifecycleChangeChipLabel(inventoryStateFieldBlockVisualState.value),
)
const inventoryUsageTypeChangeChipLabel = computed(() =>
  lifecycleChangeChipLabel(inventoryUsageTypeFieldBlockVisualState.value),
)
const inventoryStateChangeChipColor = computed(() =>
  lifecycleChangeChipColor(inventoryStateFieldBlockVisualState.value),
)
const inventoryUsageTypeChangeChipColor = computed(() =>
  lifecycleChangeChipColor(inventoryUsageTypeFieldBlockVisualState.value),
)
const inventoryStatePendingCopyClass = computed(() =>
  lifecyclePendingCopyClass(inventoryStateFieldBlockVisualState.value),
)
const inventoryUsageTypePendingCopyClass = computed(() =>
  lifecyclePendingCopyClass(inventoryUsageTypeFieldBlockVisualState.value),
)

const setInventoryStateMode = (mode: InventoryOwnershipMode) => {
  emit('update:inventoryStateMode', mode)
}

const setInventoryUsageTypeMode = (mode: InventoryOwnershipMode) => {
  emit('update:inventoryUsageTypeMode', mode)
}

const setInventoryState = (value: InventoryState) => {
  emit('update:inventoryState', value)
  emit('update:inventoryStateMode', 'MANUAL')
}

const setInventoryUsageType = (value: InventoryUsageType) => {
  emit('update:inventoryUsageType', value)
  emit('update:inventoryUsageTypeMode', 'MANUAL')
}
</script>
